import ErrorPage from 'components/error-page';
import { sendErrorToServices } from 'lib/logging';
import type { NextPage } from 'next';

// Server-side NewRelic import - only imported on server
let newrelicServer: typeof import('newrelic') | null = null;
if (typeof window === 'undefined') {
  // Only import on server-side
  import('newrelic')
    .then(nr => {
      newrelicServer = nr;
    })
    .catch(() => {
      // Silently handle import failure
      console.error('Failed to import NewRelic server module');
    });
}

interface ErrorProps {
  statusCode?: number;
  error?: Error | null;
}

const Error: NextPage<ErrorProps> = ({ statusCode, error }) => {
  return (
    <ErrorPage statusCode={statusCode || 404} error={error || undefined} />
  );
};

Error.getInitialProps = async ({ res, err, req }) => {
  // Server-side error reporting
  if (typeof window === 'undefined' && err && newrelicServer) {
    try {
      // Get additional context for server-side errors
      const ipAddress =
        req?.headers['x-forwarded-for'] ||
        req?.socket?.remoteAddress ||
        'Unknown';
      const cookies = req?.headers.cookie || '';

      // Filter cookies to only include essential ones
      const filteredCookies = cookies
        .split(';')
        .filter(cookie =>
          ['ted_session', 'OptanonConsent', '_ted_user_id'].some(name =>
            cookie.trim().startsWith(name + '=')
          )
        )
        .join(';');

      // Add custom attributes to NewRelic
      newrelicServer.addCustomAttributes({
        errorMessage: err.message,
        errorStack: err.stack || '',
        errorType: err.name,
        errorStatusCode: err.statusCode || res?.statusCode || '',
        errorIpAddress: ipAddress as string,
        errorCookies: filteredCookies,
        errorUserAgent: req?.headers['user-agent'] || 'Unknown'
      });

      newrelicServer.noticeError(err);

      // Also send to our logging service
      sendErrorToServices('Server-side Error', err, {
        errorType: err.name,
        message: `${err.message} (Status: ${err.statusCode || res?.statusCode || 500})`,
        serviceName: 'error-page',
        context: JSON.stringify({
          ipAddress,
          userAgent: req?.headers['user-agent'],
          cookies: filteredCookies
        })
      });
    } catch (e) {
      console.error('Failed to report error to NewRelic:', e);
    }
  } else if (typeof window !== 'undefined' && err) {
    // Client-side error reporting
    try {
      // Safely access window.newrelic
      const newrelicBrowser = window.newrelic;
      if (newrelicBrowser?.noticeError) {
        newrelicBrowser.noticeError(err);
      }

      // Also send to our logging service
      sendErrorToServices('Client-side Error', err, {
        errorType: err.name,
        message: err.message,
        serviceName: 'error-page'
      });
    } catch (e) {
      console.error('Failed to report error to NewRelic Browser:', e);
    }
  }

  let statusCode;
  if (res) {
    statusCode = res.statusCode;
  } else {
    statusCode = err?.statusCode || 404;
  }
  return { statusCode, error: err };
};

export default Error;
